import classnames from 'classnames/bind'
import { useRouter } from 'next/router'
import { useTranslate } from 'react-polyglot'
import { GlobalThemeColors } from '~/@types/colors'
import { GlobalGridPreset } from '~/@types/grid-preset'
import { GlobalTextPreset } from '~/@types/text-preset'
import { GlobalTextStyling } from '~/@types/text-styling'
import { FACETS_TO_RETRIEVE } from '~/lib/constants'

import {
  useAlgoliaFacets,
  useAlgoliaQueryContext,
} from '@unlikelystudio/react-ecommerce-hooks'

import RichText, { RichTextBlock } from '~/components/Abstracts/RichText'
import PopInFilters, { FacetFilters } from '~/components/PopInFilters'
import BlurredCta from '~/components/UI/BlurredCta'
import { RESULTS_GRID_LAYOUT } from '~/components/UI/ResultsGrid'
import LayoutSwitcher from '~/components/UI/ResultsGrid/LayoutSwitcher'

import { usePopIn } from '~/providers/PopInProvider'
import { useStyle } from '~/providers/StyleProvider'

import useLocale from '~/hooks/useLocale'

import { CTA, FILTER } from '~/data/dictionary'

import { CloseIcon } from '../../Icons'
import css from './styles.module.scss'

const cx = classnames.bind(css)

export interface TopBarProps {
  className?: string
  isSmall?: boolean
  hasScrolled?: boolean
  title?: string
  introduction?: RichTextBlock
  number?: number
  initialFacetFilters?: FacetFilters
  currentLayoutStep?: number
  layout?: (typeof RESULTS_GRID_LAYOUT)[keyof typeof RESULTS_GRID_LAYOUT]
  onClickLayoutButtonCallback?: (step: number) => void
}

function removeFacet(facetFilters: FacetFilters, facet: string) {
  if (typeof facetFilters === 'string') {
    return facetFilters === facet ? '' : facetFilters
  } else {
    return facetFilters
      .filter((f) => {
        if (typeof f === 'string') {
          return f !== facet
        }
        return true
      })
      ?.map((f) => {
        if (typeof f !== 'string') {
          return f?.filter((f) => f !== facet)
        }
        return f
      })
  }
}

function TopBar({
  className,
  isSmall,
  hasScrolled,
  title,
  introduction,
  initialFacetFilters,
  number,
  layout = RESULTS_GRID_LAYOUT.category,
  currentLayoutStep,
  onClickLayoutButtonCallback,
}: TopBarProps) {
  const { add } = usePopIn()
  const router = useRouter()
  const [{ facetFilters: dataFacetFilters, index }, setQuery] =
    useAlgoliaQueryContext()
  const { data } = useAlgoliaFacets()
  const locale = useLocale()
  const facetFilters = dataFacetFilters as FacetFilters
  const t = useTranslate()

  const titleStyle = useStyle({
    textPreset:
      layout !== 'search'
        ? GlobalTextPreset.Label12Primary
        : GlobalTextPreset.Label16Primary,
    textStyling: GlobalTextStyling.UpperCase,
  })

  const introductionStyle = useStyle({
    textPreset: GlobalTextPreset.RichTextEditorial,
  })

  const numberStyle = useStyle({
    textPreset:
      layout !== 'search'
        ? GlobalTextPreset.Title10Primary
        : GlobalTextPreset.Label16Primary,
    textStyling: GlobalTextStyling.UpperCase,
  })

  const gridStyle = useStyle({ grid: GlobalGridPreset.BASE_GRID })

  const facets =
    (typeof facetFilters === 'string' ? [facetFilters] : facetFilters) ?? []

  return (
    <div
      className={cx(css.TopBar, gridStyle, className, {
        isSmall,
        hasScrolled,
      })}>
      <div className={css.container}>
        <div className={css.titleWrapper}>
          {title &&
            (isSmall ? (
              <span className={cx(css.title, titleStyle)}>{title}</span>
            ) : (
              <h1 className={cx(css.title, titleStyle)}>{title}</h1>
            ))}
          {!!(number && number > 0) && (
            <span className={cx(css.number, numberStyle)}>{`(${number})`}</span>
          )}
        </div>
        {facetFilters && (
          <ul className={css.facets}>
            {facets.flat().map((facet) => {
              const [key, value] = facet.split(':')
              if (!FACETS_TO_RETRIEVE.includes(key)) return null
              return (
                <BlurredCta className={css.facetButton} isDiv key={value}>
                  <span className={css.facetValue}>{value}</span>
                  <button
                    aria-label={t(CTA.CLOSE)}
                    onClick={(e) => {
                      e.preventDefault()
                      setQuery((prevQuery) => ({
                        ...prevQuery,
                        facetFilters: removeFacet(
                          prevQuery.facetFilters as FacetFilters,
                          facet,
                        ),
                      }))
                      return false
                    }}>
                    <CloseIcon className={css.facetCloseIcon} />
                  </button>
                </BlurredCta>
              )
            })}
          </ul>
        )}
        <div className={css.toolbar}>
          <LayoutSwitcher
            className={css.layoutSwitcher}
            currentStep={currentLayoutStep}
            onClickCallback={onClickLayoutButtonCallback}
          />
          <BlurredCta
            theme={GlobalThemeColors.Black}
            className={css.cta}
            onClick={() => {
              add(
                <PopInFilters
                  defaultFacetFilters={facetFilters}
                  defaultIndex={index}
                  onSubmit={(facets, index) => {
                    router.push(
                      {
                        pathname: router.pathname,
                        query: {
                          ...router.query,
                          page: undefined,
                        },
                      },
                      null,
                      {
                        shallow: true,
                        locale,
                      },
                    )

                    setQuery((prevQueryParams) => {
                      return {
                        ...prevQueryParams,
                        index,
                        facetFilters: [
                          ...(typeof facets === 'string' ? [facets] : facets),
                          ...(typeof initialFacetFilters === 'string'
                            ? [initialFacetFilters]
                            : initialFacetFilters),
                        ],
                      }
                    })
                  }}
                  facets={data}
                />,
              )
            }}>
            {t(FILTER.REFINE)}
          </BlurredCta>
        </div>
      </div>

      {introduction && (
        <div className={cx(css.introduction, introductionStyle)}>
          <RichText render={introduction} />
        </div>
      )}
    </div>
  )
}

TopBar.defaultProps = {}

export default TopBar
